import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { Id } from "react-toastify";
import { useTranslation } from "react-i18next";
import { enviroment } from "../enviroment";
import {
  showInfoToast,
  showWarningToast,
  updateLoadingToast,
  withToasts,
} from "../presentation/components/Toast/Toast";
import { PdfNotSharpDialog } from "../presentation/components/Dialogs/pdf-not-sharp-dialog";
import type { PdfSharpOptions } from "../utils/functions/pdf-generator.service";
import {
  isPdfNotSharpError,
  isPdfSharpCancelledError,
  PdfNotSharpError,
} from "../utils/functions/pdfSharpGate";

/**
 * One proof-PDF download: `run` calls a LogoPdfGenerator.generate* method and
 * hands it the options it is given (onStatus + signal; the public screens add
 * their own refetchAll). The toast texts are the caller's existing ones.
 */
export interface SharpGatedPdfRequest {
  run: (options: PdfSharpOptions) => Promise<void>;
  loadingMessage: string;
  successMessage: string;
  errorMessage: string;
  // Passed straight to withToasts, exactly as the caller did before.
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  // Called every time a PDF was actually saved: the first run, "Try again"
  // or "Download anyway" (e.g. the lists' resetSelection).
  onDownloaded?: () => void;
  // Busy state for the runs started FROM THE DIALOG (the caller's own
  // handler is not on the stack then), e.g. the lists' setIsDownloadingPdf.
  setRerunBusy?: (busy: boolean) => void;
}

// "cancelled" = the user pressed Cancel on the loading toast while the PDF
// waited; "busy" = a download of this hook (or of the same adoptKey) was
// already running - nothing was started, 0 calls.
export type SharpGatedPdfStatus = "ok" | "not-sharp" | "cancelled" | "busy";

type Outcome =
  | { status: "ok" }
  | { status: "cancelled" }
  | { status: "not-sharp"; error: PdfNotSharpError };

interface StoppedPdf {
  request: SharpGatedPdfRequest;
  error: PdfNotSharpError;
}

// ---- Hand-over between hook instances (only used with the flag ON) --------
// The generator is static, so a download outlives the component that started
// it - and the wait can take 30-54 s. Both detail popups REMOUNT when their
// full data lands (key "list-stub" -> "summary"), and the user can close
// them. A stop that comes back to a dead instance must never be silent:
//  - an instance mounted with the SAME adoptKey (the remounted popup) takes
//    the stop over and shows the dialog,
//  - otherwise a warning toast that stays until it is closed says so.
// The same key also carries the in-flight guard across the remount, so the
// remounted popup's button cannot start a second, parallel run.
type Adopter = (stopped: StoppedPdf) => void;
const adoptersByKey = new Map<string, Adopter[]>();
const runningKeys = new Set<string>();

function registerAdopter(key: string, adopter: Adopter): () => void {
  const adopters = adoptersByKey.get(key) ?? [];
  adopters.push(adopter);
  adoptersByKey.set(key, adopters);
  return () => {
    const current = adoptersByKey.get(key);
    if (!current) return;
    const index = current.lastIndexOf(adopter);
    if (index >= 0) current.splice(index, 1);
    if (current.length === 0) adoptersByKey.delete(key);
  };
}

function latestAdopter(key: string | undefined): Adopter | undefined {
  if (key === undefined) return undefined;
  const adopters = adoptersByKey.get(key);
  return adopters?.[adopters.length - 1];
}

// The loading toast while the PDF waits: the progress text + "Cancel".
function PdfSharpWaitToast({
  text,
  cancelLabel,
  onCancel,
}: {
  text: string;
  cancelLabel: string;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span>{text}</span>
      <button
        type="button"
        className="shrink-0 font-semibold underline"
        data-testid="pdf-sharp-cancel-wait"
        onClick={onCancel}
      >
        {cancelLabel}
      </button>
    </div>
  );
}

/**
 * Flag REACT_APP_PDF_WAIT_SHARP_ENABLED. Shared by every proof-PDF call
 * site: runs the download inside withToasts, shows "Preparing sharp logo
 * images… (2/4)" + "Cancel" in the loading toast while the generator waits,
 * and - when the generator STOPS because a logo image is still not sharp -
 * opens the dialog instead of any toast (no error toast, no false "PDF
 * downloaded"). A stop never ends in silence: see the hand-over above.
 *
 * `adoptKey` (e.g. "design:123") names WHAT this instance downloads, for the
 * call sites that remount while a download can be running.
 *
 * Flag off = a plain withToasts around `run`, exactly the call sites' old
 * code; `sharpGateDialog` is always null.
 *
 * Rejects like withToasts does on a real failure (already toasted there), so
 * the callers keep their own try/catch/finally.
 */
export function usePdfDownloadWithSharpGate(config?: {
  colorHex?: string;
  adoptKey?: string;
}): {
  downloadPdf: (request: SharpGatedPdfRequest) => Promise<SharpGatedPdfStatus>;
  sharpGateDialog: JSX.Element | null;
} {
  const { t } = useTranslation();
  const [stopped, setStopped] = useState<StoppedPdf | null>(null);
  // One dialog-started run at a time (a double click on "Try again").
  const rerunning = useRef(false);
  // One download at a time per instance (a double click on the button would
  // otherwise be two parallel runs = up to 6 GetLogo per cold logo).
  const running = useRef(false);
  const mounted = useRef(false);
  const adoptKey = config?.adoptKey;

  useEffect(() => {
    if (!enviroment.pdfWaitSharpEnabled) return undefined;
    mounted.current = true;
    const release =
      adoptKey !== undefined
        ? registerAdopter(adoptKey, setStopped)
        : undefined;
    return () => {
      mounted.current = false;
      release?.();
    };
  }, [adoptKey]);

  function isBusy(): boolean {
    return (
      running.current || (adoptKey !== undefined && runningKeys.has(adoptKey))
    );
  }

  // null = busy. Otherwise the function that ends the run (idempotent: a
  // later run's guard is never released by an earlier run's second call).
  function beginRun(): (() => void) | null {
    if (isBusy()) return null;
    const key = adoptKey;
    running.current = true;
    if (key !== undefined) runningKeys.add(key);
    let ended = false;
    return () => {
      if (ended) return;
      ended = true;
      running.current = false;
      if (key !== undefined) runningKeys.delete(key);
    };
  }

  // The stop must reach the user whatever happened to this component while
  // the generator waited.
  function deliverStop(next: StoppedPdf) {
    if (mounted.current) {
      setStopped(next);
      return;
    }
    const adopter = latestAdopter(adoptKey);
    if (adopter) {
      adopter(next);
      return;
    }
    showWarningToast(t("pdfSharp.stoppedNotSharp"), false);
  }

  async function downloadPdf(
    request: SharpGatedPdfRequest,
  ): Promise<SharpGatedPdfStatus> {
    if (!enviroment.pdfWaitSharpEnabled) {
      await withToasts(
        () => request.run({}),
        request.loadingMessage,
        request.successMessage,
        request.errorMessage,
        request.setIsLoading,
      );
      request.onDownloaded?.();
      return "ok";
    }

    const endRun = beginRun();
    if (!endRun) {
      showInfoToast(t("pdfSharp.alreadyRunning"));
      return "busy";
    }

    const controller = new AbortController();
    // The user's Cancel: the generator stops waiting and makes no further
    // re-fetch. The guard is released at once - a re-fetch that is already
    // on the wire cannot be aborted, and the user must never be locked out
    // by it (a new click is a new action = a new budget).
    const cancel = () => {
      controller.abort();
      endRun();
    };
    let loadingToastId: Id | undefined;
    const onStatus = (ready: number, total: number) => {
      // Cancelled: the click closed the toast - never bring it back.
      if (loadingToastId === undefined || controller.signal.aborted) return;
      const text = t("pdfSharp.preparing", { ready, total });
      // "Cancel" only while there is something to wait for.
      updateLoadingToast(
        loadingToastId,
        ready < total ? (
          <PdfSharpWaitToast
            text={text}
            cancelLabel={t("pdfSharp.cancel")}
            onCancel={cancel}
          />
        ) : (
          text
        ),
      );
    };

    let outcome: Outcome;
    try {
      outcome = await withToasts<Outcome>(
        async () => {
          try {
            await request.run({ onStatus, signal: controller.signal });
            return { status: "ok" };
          } catch (error) {
            // Caught INSIDE the transaction so withToasts sees a resolved
            // promise: no error toast - and showSuccess below keeps the
            // success toast away.
            if (isPdfSharpCancelledError(error)) return { status: "cancelled" };
            if (isPdfNotSharpError(error)) {
              // The user's cancel wins over a stop that raced it.
              return controller.signal.aborted
                ? { status: "cancelled" }
                : { status: "not-sharp", error };
            }
            throw error;
          }
        },
        request.loadingMessage,
        request.successMessage,
        request.errorMessage,
        request.setIsLoading,
        {
          showSuccess: (result) => result.status === "ok",
          onLoadingToast: (id) => {
            loadingToastId = id;
          },
        },
      );
    } finally {
      endRun();
    }

    if (outcome.status === "not-sharp") {
      deliverStop({ request, error: outcome.error });
      return "not-sharp";
    }
    if (outcome.status === "cancelled") return "cancelled";
    request.onDownloaded?.();
    return "ok";
  }

  async function runFromDialog(
    request: SharpGatedPdfRequest,
    action: () => Promise<void>,
  ) {
    if (rerunning.current) return;
    if (isBusy()) {
      // Keep the dialog: nothing was started.
      showInfoToast(t("pdfSharp.alreadyRunning"));
      return;
    }
    rerunning.current = true;
    setStopped(null);
    request.setRerunBusy?.(true);
    try {
      await action();
    } catch (error) {
      // Already toasted by withToasts.
      console.error("Failed to generate PDF:", error);
    } finally {
      rerunning.current = false;
      request.setRerunBusy?.(false);
    }
  }

  // A new user action = a new budget (at most 3 GetLogo per cold logo).
  function handleTryAgain() {
    if (!stopped) return;
    const { request } = stopped;
    void runFromDialog(request, async () => {
      await downloadPdf(request);
    });
  }

  // Finishes the PDF with the logos the stopped run already fetched - 0 new
  // calls - and says so with a warning instead of the plain success toast.
  function handleDownloadAnyway() {
    if (!stopped) return;
    const { request, error } = stopped;
    void runFromDialog(request, async () => {
      const endRun = beginRun();
      if (!endRun) return;
      try {
        await withToasts(
          () => error.downloadAnyway(),
          request.loadingMessage,
          request.successMessage,
          request.errorMessage,
          request.setIsLoading,
          { showSuccess: () => false },
        );
      } finally {
        endRun();
      }
      showWarningToast(
        t("pdfSharp.downloadedUnsharp", { count: error.logos.length }),
      );
      request.onDownloaded?.();
    });
  }

  const sharpGateDialog = stopped ? (
    <PdfNotSharpDialog
      open
      logos={stopped.error.logos}
      colorHex={config?.colorHex}
      onTryAgain={handleTryAgain}
      onDownloadAnyway={handleDownloadAnyway}
      onCancel={() => setStopped(null)}
    />
  ) : null;

  return { downloadPdf, sharpGateDialog };
}
